import {
  IExperimentScreenLot,
  PlateInfoDTO,
  ScreenNodeInfoDTO,
} from 'business/models/Models';
import { Plate } from 'components/Experiment/constants';
import { EPlateState } from 'enums/EPlatestate';
import {
  BARCODE_DUPLICATE_ERROR,
  BARCODE_EMPTY_ERROR,
  BarcodeFieldError,
  EditBarcodeRow,
} from './constants';

export function getLotLabel(
  plate: Plate,
  screenLots?: IExperimentScreenLot[],
): string | undefined {
  const screenLotId = plate.screenLotPlate?.screenLotId;
  if (!screenLotId || !screenLots?.length) {
    return undefined;
  }

  const lot = screenLots.find((screenLot) => screenLot.id === screenLotId);
  if (!lot) {
    return undefined;
  }

  if (lot.lotNumber === 0) {
    return 'Default Lot';
  }

  return `Lot ${lot.lotNumber}`;
}

export function getScreenLotLabel(lotInfo: ScreenNodeInfoDTO): string {
  const lotName = lotInfo.lotName?.trim() ?? '';

  if (!lotName || lotName.toLowerCase() === 'defaultlot') {
    return 'Default Lot';
  }

  const lotNumberMatch = lotName.match(/^lot\s*(\d+)$/i);
  if (lotNumberMatch) {
    return `Lot ${lotNumberMatch[1]}`;
  }

  return lotName;
}

export function buildEditBarcodeRowsFromScreenNodeInfo(
  screenNodeInfo: ScreenNodeInfoDTO[],
  plateInfoById: Map<number, PlateInfoDTO>,
): EditBarcodeRow[] {
  const rows: EditBarcodeRow[] = [];

  screenNodeInfo.forEach((lotInfo) => {
    const lotLabel = getScreenLotLabel(lotInfo);

    lotInfo.plates?.forEach((plate) => {
      const plateInfo = plateInfoById.get(plate.id);
      const plateState = plateInfo?.plateState ?? 0;
      const barcode = plate.barcode?.trim() ?? plateInfo?.barcode?.trim() ?? '';

      if (!barcode || plateState >= EPlateState.Dispensed) {
        return;
      }

      rows.push({
        plateId: plate.id,
        plateLabel: String(plate.id),
        lotLabel,
        barcode,
        initialBarcode: barcode,
        plateNumber: plate.plateNumber,
      });
    });
  });

  return rows.sort((a, b) => a.plateNumber - b.plateNumber);
}

function isPlateEligibleForBarcodeEdit(plate: Plate): boolean {
  const hasBarcode = Boolean(plate.barCode?.trim());
  const isNotDispensed = plate.state < EPlateState.Dispensed;

  return hasBarcode && isNotDispensed;
}

function toEditBarcodeRow(
  plate: Plate,
  screenLots?: IExperimentScreenLot[],
): EditBarcodeRow {
  const barcode = plate.barCode?.trim() ?? '';

  return {
    plateId: plate.plateId,
    plateLabel: String(plate.plateId),
    lotLabel: getLotLabel(plate, screenLots),
    barcode,
    initialBarcode: barcode,
    plateNumber: plate.plateNumber,
  };
}

export function buildEditBarcodeRows(
  plateDto: Plate[] | undefined,
  screenLots?: IExperimentScreenLot[],
): EditBarcodeRow[] {
  if (!plateDto?.length) {
    return [];
  }

  const eligiblePlates = plateDto.filter(isPlateEligibleForBarcodeEdit);
  const sortedPlates = [...eligiblePlates].sort((a, b) => a.plateNumber - b.plateNumber);
  const rows = sortedPlates.map((plate) => toEditBarcodeRow(plate, screenLots));

  return rows;
}

export function mergePlateBarcodesFromPlateInfo(
  plateDto: Plate[] | undefined,
  plateInfoList: PlateInfoDTO[],
): Plate[] | undefined {
  if (!plateDto?.length) {
    return plateDto;
  }

  const barcodeById = new Map(
    plateInfoList.map((plate) => [plate.id, plate.barcode?.trim() ?? '']),
  );

  return plateDto.map((plate) => {
    if (!barcodeById.has(plate.plateId)) {
      return plate;
    }

    return {
      ...plate,
      barCode: barcodeById.get(plate.plateId) ?? '',
    };
  });
}

export function getListDuplicatePlateIds(rows: EditBarcodeRow[]): Set<number> {
  const barcodeToPlateIds = new Map<string, number[]>();

  rows.forEach((row) => {
    const trimmedBarcode = row.barcode.trim();
    if (!trimmedBarcode) {
      return;
    }

    const plateIds = barcodeToPlateIds.get(trimmedBarcode) ?? [];
    plateIds.push(row.plateId);
    barcodeToPlateIds.set(trimmedBarcode, plateIds);
  });

  const duplicatePlateIds = new Set<number>();
  barcodeToPlateIds.forEach((plateIds) => {
    if (plateIds.length > 1) {
      plateIds.forEach((plateId) => duplicatePlateIds.add(plateId));
    }
  });

  return duplicatePlateIds;
}

export function getErrorMessage(fieldError: BarcodeFieldError): string {
  if (fieldError === 'empty') {
    return BARCODE_EMPTY_ERROR;
  }
  if (fieldError === 'duplicate') {
    return BARCODE_DUPLICATE_ERROR;
  }
  return '';
}
